import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  useComputedColorScheme,
  useMantineTheme,
  Group,
} from '@mantine/core';
import { buttonTextColor } from 'utils/buttonUtils';

interface ClaimActionConfirmModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  body: string;
  emphasizePhrase?: string;
  children?: React.ReactNode;
}

export const ClaimActionConfirmModal: React.FC<
  ClaimActionConfirmModalProps
> = ({ open, setOpen, onConfirm, title, body, emphasizePhrase, children }) => {
  const mode = useComputedColorScheme();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);

  // Build highlighted body with emphasizePhrase bold + primary color
  const highlightedBody = useMemo(() => {
    if (!emphasizePhrase) return body;
    const phrase = emphasizePhrase.trim();
    if (!phrase) return body;

    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = body.split(regex);
    if (parts.length === 1) return body;

    const isDark = mode === 'dark';
    const palette = theme.colors[theme.primaryColor] || [];
    const shade = isDark ? palette[4] : palette[6];

    return parts.map((part, idx) =>
      regex.test(part) ? (
        <Text key={idx} span fw={700} fs="italic" c={shade}>
          {part}
        </Text>
      ) : (
        <Text key={idx} span>
          {part}
        </Text>
      )
    );
  }, [body, emphasizePhrase, mode, theme]);

  // Close
  const handleClose = () => setOpen(false);

  // Confirm with loading state
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await Promise.resolve(onConfirm());
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Keyboard: Cmd/Ctrl+Enter = confirm
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      closeOnEscape
      withCloseButton
      size={children ? 'md' : 'sm'}
      centered
      radius="lg"
      shadow="xl"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      title={<Text fw={700}>{title}</Text>}
      styles={{
        content: {
          background:
            mode === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border:
            mode === 'dark'
              ? `1px solid ${theme.colors.dark[4]}`
              : `1px solid ${theme.colors.gray[3]}`,
        },
        header: { paddingBottom: 0 },
        title: {
          color: mode === 'dark' ? theme.white : undefined,
        },
        close: {
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
          }`,
          background: mode === 'dark' ? theme.colors.dark[6] : theme.white,
          borderRadius: 10,
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${
              mode === 'dark' ? theme.colors.blue[6] : theme.colors.blue[5]
            }`,
          },
        },
      }}
    >
      <Stack gap="md" align="center">
        <Text size="sm" ta="center">
          {Array.isArray(highlightedBody) ? highlightedBody : highlightedBody}
        </Text>

        {children}

        <Group justify="center" mt="sm" gap="lg">
          <Button
            color="gray"
            c={buttonTextColor(mode, false)}
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            c={buttonTextColor(mode, false)}
            onClick={handleConfirm}
            loading={loading}
          >
            Confirm
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          Cmd/Ctrl + Enter to confirm
        </Text>
      </Stack>
    </Modal>
  );
};
